import type {EntityLevelControl} from "../../types";

// Entity Level Controls
export const ENTITY_LEVEL_CONTROLS: EntityLevelControl[] = [
  {
    id: "elc-control-environment",
    name: "Control Environment",
    description: "",
    sections: [
      {
        title: "Organisational Structure & Tone at the Top",
        description: `## Commitment to Integrity and Ethical Values

The organisation maintains a formal **Code of Conduct** that is reviewed and updated annually by the Board of Directors. All employees, contractors, and third-party agents are required to acknowledge the Code upon hire and annually thereafter. The current version (v4.0, effective January 2026) covers conflicts of interest, anti-bribery, data protection, and financial reporting integrity.

Senior leadership reinforces ethical expectations through:

- **Quarterly town halls** where the CEO and CFO explicitly address integrity, compliance, and the importance of accurate financial reporting
- **Annual ethics training** — mandatory for all employees, with supplemental modules for finance, procurement, and IT staff
- **Whistleblower protection** — a zero-retaliation policy is embedded in the Code of Conduct and reinforced in onboarding materials

The ethics training completion rate for the current cycle is **94%**, with the remaining 6% comprising employees on extended leave. The General Counsel's office monitors completion and escalates non-compliance to divisional leadership.

## Tone at the Top

The CFO chairs a monthly **Financial Reporting Integrity Forum** attended by the Controller, Head of Internal Audit, and business unit finance leads. The forum reviews emerging accounting issues, regulatory developments, and the status of open audit findings. Minutes are retained and made available to the Audit Committee upon request.`,
      },
      {
        title: "Governance & Board Oversight",
        description: `## Governance Structure

The organisation operates a **three-lines governance model**. The Board of Directors meets quarterly and has delegated financial reporting oversight to the **Audit Committee**, which comprises three independent non-executive directors (two of whom hold CPA certifications). The Audit Committee is chaired by a former CFO with extensive public-company experience.

## Board and Audit Committee Oversight

Management presents a **quarterly reporting package** to the Audit Committee covering:

- Financial performance and variance analysis
- Status of open issues and remediation progress
- Internal audit engagement results and upcoming plan
- Regulatory correspondence and filing status
- Fraud/ethics hotline summary and investigation outcomes

Critical financial reporting issues identified between scheduled meetings are escalated to the Audit Committee chair within 48 hours.`,
        diagramId: "hardcoded-gov-001",
      },
      {
        title: "Human Resource Policies & Competence",
        description: `## Competence and Accountability

The organisation has established formal **competence requirements** for all roles with financial reporting responsibilities. Job descriptions for finance, accounting, and IT positions specify required qualifications, certifications, and experience levels.

Key HR policies supporting the control environment include:

- **Background checks** — all finance and IT staff undergo pre-employment verification of qualifications, references, and (where legally permitted) criminal history
- **Continuing professional development** — finance staff are required to complete a minimum of 40 hours of CPD annually, with records maintained by HR
- **Annual performance evaluations** — include assessment of adherence to internal controls and ethical standards as a specific evaluation criterion
- **Succession planning** — critical financial reporting roles (Controller, Head of Tax, Treasury Manager) have documented succession plans reviewed annually by the CFO

## Annual Competence Assessment

The CFO conducts an **annual competence assessment** of key financial reporting personnel, evaluating technical knowledge, judgment quality, and control awareness. Results are documented and inform training plans for the subsequent year. The most recent assessment (January 2026) identified no significant competence gaps, though it recommended additional IFRS 17 training for the insurance accounting team ahead of the new product launch.`,
      },
    ],
  },
  {
    id: "elc-monitoring-governance",
    name: "Monitoring of Controls",
    description: "",
    sections: [
      {
        title: "Ongoing Monitoring",
        description: `## Management's Ongoing Monitoring Activities

Management has established a framework of **ongoing monitoring activities** designed to assess the continued effectiveness of internal controls over financial reporting. These activities are integrated into normal business operations and provide timely information about control performance.

Key ongoing monitoring mechanisms include:

- **Monthly KPI Dashboard** — the Controller reviews a dashboard of key performance indicators covering transaction processing accuracy, reconciliation completion rates, exception volumes, and control execution timeliness. Metrics trending outside established thresholds trigger investigation and escalation.
- **Automated exception alerting** — the ERP system (SAP S/4HANA) generates real-time alerts for predefined exception conditions including duplicate payments, unusual journal entries, segregation-of-duties violations, and transactions exceeding approval limits. Alerts are routed to the relevant control owner for investigation within 24 hours.
- **Management self-assessment** — process owners complete a quarterly self-assessment of control effectiveness for their area, identifying any control breakdowns, near-misses, or changes in risk profile. Results are consolidated by the Controller and reported to the CFO.

## Exception Monitoring Configuration

The automated exception monitoring rules were initially configured during the SAP S/4HANA implementation in 2023 and have been updated incrementally as new transaction types and business processes were onboarded. The current rule set covers **47 exception conditions** across accounts payable, accounts receivable, general ledger, and payroll modules.`,
      },
      {
        title: "Separate Evaluations",
        description: `## Internal Audit Function

Internal Audit reports **functionally to the Audit Committee** and administratively to the CFO, preserving independence. The function executes a risk-based annual audit plan that covers financial reporting, operational processes, and compliance. Key characteristics:

- **Full-scope** mandate covering financial, operational, and compliance audits
- Annual plan approved by the Audit Committee with quarterly progress reporting
- Staffed with 4 FTEs; co-sourced with external specialists for IT audit and specialised testing
- Direct, unrestricted access to all records, personnel, and systems

## Issue Tracking and Remediation

Identified issues are logged in a **centralised tracker** with severity-based timelines (Critical: 30 days, Moderate: 60 days, Low: 90 days). Management reviews the tracker monthly and overdue items are escalated to the Audit Committee. Issues rated moderate or above are re-tested before closure.

The tracker is currently spreadsheet-based and lacks automated reminders. A GRC platform implementation is planned for Q3 2026 to introduce workflow automation and real-time dashboards.

## Monitoring of Outsourced Service Providers

Management obtains and reviews **SOC 1 Type II reports** annually for all outsourced providers that process financially significant data (currently: cloud payroll provider and hosted ERP infrastructure). Reviews include evaluation of complementary user entity obligations and follow-up on any qualified opinions or noted exceptions.`,
      },
    ],
  },
  {
    id: "elc-it-environment",
    name: "IT Environment & GITCs",
    description: `## IT Landscape

The organisation's financially significant IT environment centres on **SAP S/4HANA** as the core ERP system, supplemented by **Oracle EPM** for consolidation and reporting, **BlackLine** for account reconciliations, and **Workday** for HR and payroll. All applications authenticate through **Azure AD** with enforced SSO and MFA.

## Application Architecture

The technology stack is primarily **cloud-hosted** (SAP on AWS IaaS, remaining applications SaaS) with no on-premise infrastructure for financially significant systems. Key integration points include:

- **Workday → SAP**: Payroll journal feed via encrypted SFTP
- **Bloomberg → SAP**: Daily FX rate feed with automated tolerance checks
- **SAP → Oracle EPM**: GL trial balance and sub-ledger feeds for consolidation
- **SAP Concur → SAP**: Employee expense AP feed with automated policy compliance checks
- **BlackLine ↔ SAP/EPM**: Automated transaction matching and reconciliation

## User Access Management

Access requests are submitted through **ServiceNow**, requiring documented approval from the user's manager and system owner before provisioning. **Quarterly access reviews** are performed by system owners to certify continued need and appropriate role assignment. Terminated employee accounts are disabled via automated integration between Workday and Azure AD.

## Change Management

All production changes require approval from the **Change Advisory Board (CAB)**, including evidence of testing in a non-production environment. Developers do not have direct access to deploy to production; all deployments are executed by a separate release management team through **Azure DevOps** pipelines. Database schema changes are managed through **Liquibase** with full audit trail.

## Privileged Access

Privileged accounts are vaulted in **CyberArk PAM** with session recording enabled. All privileged activity is logged and reviewed monthly by IT Security, with anomalous activity triggering investigation within 24 hours. The organisation currently has 14 privileged accounts across production systems.

## IT Operations

- **Backup & DR**: Nightly backups with weekly restore tests and annual disaster recovery exercises
- **Vulnerability Management**: Quarterly external and internal scans via **Qualys**; critical findings remediated within 30 days
- **Security Monitoring**: Centralised log aggregation and alerting via **Splunk SIEM**`,
    diagramId: "hardcoded-icfr-001",
  },
  {id: "elc-risk-assessment", name: "Entity's Risk Assessment Process", description: ""},
];
